<script setup>

import {computed, onMounted, reactive, ref} from "vue";

const props = defineProps({
    location: {
        type: String,
        required: true,
    }
})

const savingState = ref('')
const editingTheme = ref(false)
const currentTheme = ref(null)
const logoUrl = ref('')
const navigation = ref([])
const availableThemes = ref({})
const selectingTheme = ref(false)
const accountCreationEnabled = ref({})
const previewSize = ref('desktop')
const selectingNavBarBgColor = ref(false)
const selectingNavBarTextColor = ref(false)

const settings = reactive({
    desktop: {
        paddingTop: 16,
        paddingBottom: 16,
        logoWidth: 96,
        navItemFontSize: 14,
        navItemGap: 20,
    },
    mobile: {
        paddingTop: 14,
        paddingBottom: 14,
        logoWidth: 32,
        navItemFontSize: 15,
        navItemGap: 12,
    },
    navBarBgType: 'solidColor',
    navBarBgColor: '#ffffff',
    navBarTextColor: '#3f3f3f',
    fontFamily: 'Open Sans',
    fixedHeader: false,
    fullWidth: false,
    maxWidth: 1024,
    showLoginButton: true,
    dropShadow: {
        enabled: false,
        style: 'subtle'
    }
})

const loadTheme = async () => {
    const res = await axios.get(route('settings.appearance.headerTheme.show', {
        location: props.location,
    }));
    currentTheme.value = res.data.theme;
    logoUrl.value = res.data.logo_url;
    navigation.value = res.data.navigation;
    availableThemes.value = res.data.available_themes;
    accountCreationEnabled.value = res.data.account_creation_enabled;

    setSettings(res.data.settings)
}

const setSettings = (loadedSettings) => {
    if (loadedSettings.desktop) {
        settings.desktop.paddingTop = loadedSettings.desktop.paddingTop;
        settings.desktop.paddingBottom = loadedSettings.desktop.paddingBottom;
        settings.desktop.logoWidth = loadedSettings.desktop.logoWidth;
        settings.desktop.navItemFontSize = loadedSettings.desktop.navItemFontSize;
        settings.desktop.navItemGap = loadedSettings.desktop.navItemGap;
    }
    if (loadedSettings.mobile) {
        settings.mobile.paddingTop = loadedSettings.mobile.paddingTop;
        settings.mobile.paddingBottom = loadedSettings.mobile.paddingBottom;
        settings.mobile.logoWidth = loadedSettings.mobile.logoWidth;
        settings.mobile.navItemFontSize = loadedSettings.mobile.navItemFontSize;
        settings.mobile.navItemGap = loadedSettings.mobile.navItemGap;
    }
    if (loadedSettings.fontFamily) {
        settings.fontFamily = loadedSettings.fontFamily;
        setDocumentFonts(settings.fontFamily);
    }
    if (loadedSettings.navBarBgType) {
        settings.navBarBgType = loadedSettings.navBarBgType;
    }
    if (loadedSettings.navBarBgColor) {
        settings.navBarBgColor = loadedSettings.navBarBgColor;
    }
    if (loadedSettings.navBarTextColor) {
        settings.navBarTextColor = loadedSettings.navBarTextColor;
    }
    if (loadedSettings.maxWidth) {
        settings.maxWidth = loadedSettings.maxWidth;
    }
    if (loadedSettings.fullWidth) {
        settings.fullWidth = loadedSettings.fullWidth;
    }
    if (loadedSettings.fixedHeader) {
        settings.fixedHeader = loadedSettings.fixedHeader;
    }
    if (loadedSettings.showLoginButton !== undefined) {
        settings.showLoginButton = loadedSettings.showLoginButton;
    }
    if (loadedSettings.dropShadow) {
        settings.dropShadow.enabled = loadedSettings.dropShadow.enabled;
        settings.dropShadow.style = loadedSettings.dropShadow.style;
    }
}

const submit = async () => {
    savingState.value = 'waiting';
    const headerThemeResponse = await axios.post(route('settings.appearance.headerTheme.update', props.location), {
        theme: currentTheme.value,
        settings: settings,
    });

    if (headerThemeResponse.data.success !== true) {
        savingState.value = 'error';
        notificationStore.addNotification({
            type: 'error',
            title: _mft('error:error.generalOccurred'),
            message: headerThemeResponse.data.message,
            dismissAfter: 0,
        })
    } else {
        savingState.value = 'success';
    }
}

const setFontFamily = (family) => {
    if (!family) {
        return
    }
    settings.fontFamily = family

    setDocumentFonts(settings.fontFamily)
}

const selectTheme = (theme) => {
    currentTheme.value = theme.name;
    selectingTheme.value = false;
}

const setDocumentFonts = (fontFamily) => {
    WebFont.load({
        google: {
            families: [fontFamily + ':400,600,700']
        }
    });

    document.getElementById('pagebuilder-font').innerHTML = `
            #top-navigation {
                font-family: ${fontFamily}, Open Sans;
            }

            .font-selector-preview {
                font-family: ${fontFamily}, Open Sans;
            }
        `
};


const cancel = () => {
    editingTheme.value = false;
    loadTheme();
}

const currentThemeName = computed(() => {
    if (Object.values(availableThemes.value).length === 0) {
        return ''
    }
    const theme = Object.values(availableThemes.value).find(t => t.name === currentTheme.value);
    return theme ? theme.title : _mft('setting:appearance.theme.default');
})

const title = computed(() => {
    if (props.location === 'website') {
        return _mft('setting:appearance.theme.website')
    }
    if (props.location === 'customer_area') {
        return _mft('setting:appearance.theme.customerArea')
    }
})

const selectedThemeComponent = computed(() => {
    if (['theme-a', 'theme-b', 'theme-c', 'theme-d'].includes(currentTheme.value)) {
        return 'customer-area-navigation-bar-themed'
    }
    if (currentTheme.value === 'navigationBarDefault') {
        return 'customer-area-navigation-bar-default'
    }

    return 'customer-area-navigation-bar-default'
})

onMounted(() => {
    loadTheme()
})

</script>

<template>
    <div>
        <template v-if="editingTheme">
            <div class="fixed inset-0 bg-black/80 z-modal-base">
                <div
                    v-if="selectingTheme"
                    class="fixed inset-0 z-modal-backdrop"
                    @click="selectingTheme = false"
                ></div>
                <div class="bg-white">
                    <div class="flex">
                        <div class="w-72 grow-0 shrink-0 h-screen p-4 border-r overflow-y-scroll">
                            <h5 class="mb-2">{{ _mft('setting:appearance.theme.themeSettings') }}</h5>
                            <input-label :label="props.location === 'website' ? _mft('setting:appearance.theme.website') : _mft('setting:appearance.theme.customerArea')" />

                            <div class="relative mt-1 z-modal">
                                <button-base
                                    style-type="default"
                                    @click="selectingTheme = ! selectingTheme"
                                    class="w-full border rounded-wl"
                                >
                                    <template #icon>
                                        <span
                                            v-if="currentTheme === 'default'"
                                            class="block py-4"
                                        >
                                            {{ _mft('setting:appearance.theme.default') }}
                                        </span>

                                        <svg class="-mt-1 -mb-1" v-if="currentTheme === 'theme-a'" viewBox="0 0 384 128" xmlns="http://www.w3.org/2000/svg">
                                            <g id="theme-a">
                                                <path id="Rectangle" fill="#8f8f8f" fill-rule="evenodd" stroke="none" d="M 79 72 L 217 72 L 217 56 L 79 56 Z"/>
                                                <path id="Rectangle-copy" fill="#8f8f8f" fill-rule="evenodd" stroke="none" d="M 8 89 L 70 89 L 70 39 L 8 39 Z"/>
                                            </g>
                                        </svg>

                                        <svg class="-mt-1 -mb-1" v-if="currentTheme === 'theme-b'" viewBox="0 0 384 128" xmlns="http://www.w3.org/2000/svg">
                                            <g id="theme-b">
                                                <path id="Rectangle" fill="#8f8f8f" fill-rule="evenodd" stroke="none" d="M 123 72 L 261 72 L 261 56 L 123 56 Z"/>
                                                <path id="path1" fill="#8f8f8f" fill-rule="evenodd" stroke="none" d="M 8 89 L 70 89 L 70 39 L 8 39 Z"/>
                                            </g>
                                        </svg>

                                        <svg class="-mt-1 -mb-1" v-if="currentTheme === 'theme-c'" viewBox="0 0 384 128" xmlns="http://www.w3.org/2000/svg">
                                            <g id="theme-c">
                                                <path id="Rectangle" fill="#8f8f8f" fill-rule="evenodd" stroke="none" d="M 233 72 L 371 72 L 371 56 L 233 56 Z"/>
                                                <path id="path1" fill="#8f8f8f" fill-rule="evenodd" stroke="none" d="M 8 89 L 70 89 L 70 39 L 8 39 Z"/>
                                            </g>
                                        </svg>

                                        <svg class="-mt-1 -mb-1" v-if="currentTheme === 'theme-d'" viewBox="0 0 384 128" xmlns="http://www.w3.org/2000/svg">
                                            <g id="theme-d">
                                                <path id="Rectangle" fill="#8f8f8f" fill-rule="evenodd" stroke="none" d="M 90 108 L 294 108 L 294 92 L 90 92 Z"/>
                                                <path id="path1" fill="#8f8f8f" fill-rule="evenodd" stroke="none" d="M 161 74 L 223 74 L 223 24 L 161 24 Z"/>
                                            </g>
                                        </svg>
                                    </template>
                                </button-base>

                                <div
                                    v-if="selectingTheme"
                                    class="absolute top-full mt-2 left-0 right-0 bg-white z-20 border rounded-wl divide-y shadow-xl flex flex-col"
                                >
                                    <button
                                        v-for="(theme, index) in Object.values(availableThemes)"
                                        @click="selectTheme(theme)"
                                        class="w-full hover:bg-gray-100"
                                        :class="theme.name === currentTheme ? 'bg-gray-200' : ''"
                                    >
                                        <span
                                            v-if="theme.name === 'default'"
                                            class="block py-4"
                                        >
                                            {{ _mft('setting:appearance.theme.default') }}
                                        </span>

                                        <svg
                                            v-if="theme.name === 'theme-a'"
                                            viewBox="0 0 384 128"
                                            xmlns="http://www.w3.org/2000/svg"
                                            class="mx-4"
                                        >
                                            <g id="theme-a">
                                                <path id="Rectangle" fill="#8f8f8f" fill-rule="evenodd" stroke="none" d="M 79 72 L 217 72 L 217 56 L 79 56 Z"/>
                                                <path id="Rectangle-copy" fill="#8f8f8f" fill-rule="evenodd" stroke="none" d="M 8 89 L 70 89 L 70 39 L 8 39 Z"/>
                                            </g>
                                        </svg>

                                        <svg
                                            v-if="theme.name === 'theme-b'"
                                            viewBox="0 0 384 128"
                                            xmlns="http://www.w3.org/2000/svg"
                                            class="mx-4"
                                        >
                                            <g id="theme-b">
                                                <path id="Rectangle" fill="#8f8f8f" fill-rule="evenodd" stroke="none" d="M 123 72 L 261 72 L 261 56 L 123 56 Z"/>
                                                <path id="path1" fill="#8f8f8f" fill-rule="evenodd" stroke="none" d="M 8 89 L 70 89 L 70 39 L 8 39 Z"/>
                                            </g>
                                        </svg>

                                        <svg
                                            v-if="theme.name === 'theme-c'"
                                            viewBox="0 0 384 128"
                                            xmlns="http://www.w3.org/2000/svg"
                                            class="mx-4"
                                        >
                                            <g id="theme-c">
                                                <path id="Rectangle" fill="#8f8f8f" fill-rule="evenodd" stroke="none" d="M 233 72 L 371 72 L 371 56 L 233 56 Z"/>
                                                <path id="path1" fill="#8f8f8f" fill-rule="evenodd" stroke="none" d="M 8 89 L 70 89 L 70 39 L 8 39 Z"/>
                                            </g>
                                        </svg>

                                        <svg
                                            v-if="theme.name === 'theme-d'"
                                            viewBox="0 0 384 128"
                                            xmlns="http://www.w3.org/2000/svg"
                                            class="mx-4"
                                        >
                                            <g id="theme-d">
                                                <path id="Rectangle" fill="#8f8f8f" fill-rule="evenodd" stroke="none" d="M 90 108 L 294 108 L 294 92 L 90 92 Z"/>
                                                <path id="path1" fill="#8f8f8f" fill-rule="evenodd" stroke="none" d="M 161 74 L 223 74 L 223 24 L 161 24 Z"/>
                                            </g>
                                        </svg>

                                    </button>
                                </div>
                            </div>



                            <div v-if="currentTheme !== 'default'" class="mt-4">
                                <toggle-switch
                                    v-if="props.location === 'website'"
                                    v-model:content="settings.showLoginButton"
                                    :label="_mft('setting:appearance.header.showLoginButton')"
                                ></toggle-switch>

                                <toggle-switch
                                    v-model:content="settings.dropShadow.enabled"
                                    :label="_mft('setting:appearance.header.dropShadow')"
                                ></toggle-switch>

                                <hr class="-mx-4 my-4">

                                <toggle-switch
                                    v-model:content="settings.fixedHeader"
                                    :label="_mft('setting:appearance.header.fixedHeader')"
                                ></toggle-switch>

                                <toggle-switch
                                    v-if="previewSize === 'desktop'"
                                    v-model:content="settings.fullWidth"
                                    :label="_mft('setting:appearance.header.fullWidth')"
                                ></toggle-switch>

                                <hr class="-mx-4 mt-4">
                                <h5 class="mt-4 mb-2">{{ _mft('setting:appearance.theme.measures') }}</h5>
                                <div
                                    v-if="previewSize === 'desktop'"
                                >
                                    <div>
                                        <div class="flex justify-between space-x-4 mb-1.5">
                                            <input-label :label="_mft('setting:appearance.header.logoWidth')"></input-label>
                                            <div>
                                                <button-default
                                                    @click="settings.desktop.logoWidth = 96"
                                                    size="xs"
                                                >
                                                    <i class="fa-regular fa-rotate-left"></i>
                                                </button-default>
                                            </div>
                                        </div>
                                        <div class="flex space-x-2 items-center mb-4">
                                            <div class="w-full flex space-x-2 items-center">
                                                <input-range
                                                    v-model:content="settings.desktop.logoWidth"
                                                    min="0"
                                                    max="400"
                                                    class="w-full mt-1"
                                                ></input-range>
                                                <input-number
                                                    class="w-24 !mb-0"
                                                    input-class="text-xs"
                                                    v-model:content="settings.desktop.logoWidth"
                                                ></input-number>
                                            </div>
                                        </div>
                                    </div>

                                    <div v-if="! settings.fullWidth">
                                        <div class="flex justify-between space-x-4 mb-1.5">
                                            <input-label :label="_mft('setting:appearance.header.navigationMaxWidth')"></input-label>
                                            <div>
                                                <button-default
                                                    @click="settings.maxWidth = 1024"
                                                    size="xs"
                                                >
                                                    <i class="fa-regular fa-rotate-left"></i>
                                                </button-default>
                                            </div>
                                        </div>
                                        <div class="flex space-x-2 items-center mb-4">
                                            <div class="w-full flex space-x-2 items-center">
                                                <input-range
                                                    v-model:content="settings.maxWidth"
                                                    min="768"
                                                    max="2560"
                                                    class="w-full mt-1"
                                                ></input-range>
                                                <input-number
                                                    class="w-24 !mb-0"
                                                    input-class="text-xs"
                                                    v-model:content="settings.maxWidth"
                                                ></input-number>
                                            </div>
                                        </div>
                                    </div>

                                    <div>
                                        <div class="flex justify-between space-x-4 mb-1.5">
                                            <input-label :label="_mft('setting:appearance.header.navigationItemGap')"></input-label>
                                            <div>
                                                <button-default
                                                    @click="settings.desktop.navItemGap = 20"
                                                    size="xs"
                                                >
                                                    <i class="fa-regular fa-rotate-left"></i>
                                                </button-default>
                                            </div>
                                        </div>
                                        <div class="flex space-x-2 items-center mb-4">
                                            <div class="w-full flex space-x-2 items-center">
                                                <input-range
                                                    v-model:content="settings.desktop.navItemGap"
                                                    min="1"
                                                    max="80"
                                                    class="w-full mt-1"
                                                ></input-range>
                                                <input-number
                                                    class="w-24 !mb-0"
                                                    input-class="text-xs"
                                                    v-model:content="settings.desktop.navItemGap"
                                                ></input-number>
                                            </div>
                                        </div>
                                    </div>

                                    <div>
                                        <div class="flex justify-between space-x-4 mb-1.5">
                                            <input-label :label="_mft('setting:appearance.header.paddingTop')"></input-label>
                                            <div>
                                                <button-default
                                                    @click="settings.desktop.paddingTop = 16"
                                                    size="xs"
                                                >
                                                    <i class="fa-regular fa-rotate-left"></i>
                                                </button-default>
                                            </div>
                                        </div>
                                        <div class="flex space-x-2 items-center mb-4">
                                            <div class="w-full flex space-x-2 items-center">
                                                <input-range
                                                    v-model:content="settings.desktop.paddingTop"
                                                    min="0"
                                                    max="80"
                                                    class="w-full mt-1"
                                                ></input-range>
                                                <input-number
                                                    class="w-24 !mb-0"
                                                    input-class="text-xs"
                                                    v-model:content="settings.desktop.paddingTop"
                                                ></input-number>
                                            </div>
                                        </div>
                                    </div>

                                    <div>
                                        <div class="flex justify-between space-x-4 mb-1.5">
                                            <input-label :label="_mft('setting:appearance.header.paddingBottom')"></input-label>
                                            <div>
                                                <button-default
                                                    @click="settings.desktop.paddingBottom = 16"
                                                    size="xs"
                                                >
                                                    <i class="fa-regular fa-rotate-left"></i>
                                                </button-default>
                                            </div>
                                        </div>
                                        <div class="flex space-x-2 items-center mb-4">
                                            <div class="w-full flex space-x-2 items-center">
                                                <input-range
                                                    v-model:content="settings.desktop.paddingBottom"
                                                    min="0"
                                                    max="80"
                                                    class="w-full mt-1"
                                                ></input-range>
                                                <input-number
                                                    class="w-24 !mb-0"
                                                    input-class="text-xs"
                                                    v-model:content="settings.desktop.paddingBottom"
                                                ></input-number>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div
                                    v-if="previewSize === 'mobile'"
                                >
                                    <div>
                                        <div class="flex justify-between space-x-4 mb-1.5">
                                            <input-label :label="_mft('setting:appearance.header.logoWidth')"></input-label>
                                            <div>
                                                <button-default
                                                    @click="settings.mobile.logoWidth = 32"
                                                    size="xs"
                                                >
                                                    <i class="fa-regular fa-rotate-left"></i>
                                                </button-default>
                                            </div>
                                        </div>
                                        <div class="flex space-x-2 items-center mb-4">
                                            <div class="w-full flex space-x-2 items-center">
                                                <input-range
                                                    v-model:content="settings.mobile.logoWidth"
                                                    min="0"
                                                    max="120"
                                                    class="w-full mt-1"
                                                ></input-range>
                                                <input-number
                                                    class="w-24 !mb-0"
                                                    input-class="text-xs"
                                                    v-model:content="settings.mobile.logoWidth"
                                                ></input-number>
                                            </div>
                                        </div>
                                    </div>

                                    <div>
                                        <div class="flex justify-between space-x-4 mb-1.5">
                                            <input-label :label="_mft('setting:appearance.header.navigationItemGap')"></input-label>
                                            <div>
                                                <button-default
                                                    @click="settings.mobile.navItemGap = 12"
                                                    size="xs"
                                                >
                                                    <i class="fa-regular fa-rotate-left"></i>
                                                </button-default>
                                            </div>
                                        </div>
                                        <div class="flex space-x-2 items-center mb-4">
                                            <div class="w-full flex space-x-2 items-center">
                                                <input-range
                                                    v-model:content="settings.mobile.navItemGap"
                                                    min="1"
                                                    max="80"
                                                    class="w-full mt-1"
                                                ></input-range>
                                                <input-number
                                                    class="w-24 !mb-0"
                                                    input-class="text-xs"
                                                    v-model:content="settings.mobile.navItemGap"
                                                ></input-number>
                                            </div>
                                        </div>
                                    </div>

                                    <div>
                                        <div class="flex justify-between space-x-4 mb-1.5">
                                            <input-label :label="_mft('setting:appearance.header.paddingTop')"></input-label>
                                            <div>
                                                <button-default
                                                    @click="settings.mobile.paddingTop = 14"
                                                    size="xs"
                                                >
                                                    <i class="fa-regular fa-rotate-left"></i>
                                                </button-default>
                                            </div>
                                        </div>
                                        <div class="flex space-x-2 items-center mb-4">
                                            <div class="w-full flex space-x-2 items-center">
                                                <input-range
                                                    v-model:content="settings.mobile.paddingTop"
                                                    min="0"
                                                    max="80"
                                                    class="w-full mt-1"
                                                ></input-range>
                                                <input-number
                                                    class="w-24 !mb-0"
                                                    input-class="text-xs"
                                                    v-model:content="settings.mobile.paddingTop"
                                                ></input-number>
                                            </div>
                                        </div>
                                    </div>

                                    <div>
                                        <div class="flex justify-between space-x-4 mb-1.5">
                                            <input-label :label="_mft('setting:appearance.header.paddingBottom')"></input-label>
                                            <div>
                                                <button-default
                                                    @click="settings.mobile.paddingBottom = 14"
                                                    size="xs"
                                                >
                                                    <i class="fa-regular fa-rotate-left"></i>
                                                </button-default>
                                            </div>
                                        </div>
                                        <div class="flex space-x-2 items-center mb-4">
                                            <div class="w-full flex space-x-2 items-center">
                                                <input-range
                                                    v-model:content="settings.mobile.paddingBottom"
                                                    min="0"
                                                    max="80"
                                                    class="w-full mt-1"
                                                ></input-range>
                                                <input-number
                                                    class="w-24 !mb-0"
                                                    input-class="text-xs"
                                                    v-model:content="settings.mobile.paddingBottom"
                                                ></input-number>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <hr class="-mx-4 mt-4">
                                <h5 class="mt-4 mb-2">{{ _mft('setting:appearance.theme.text') }}</h5>

                                <div class="mb-4">
                                    <input-label :label="_mft('setting:appearance.header.fontFamily')"></input-label>
                                    <block-editor-font-options
                                        class="w-full"
                                        :font="settings.fontFamily"
                                        :family-only="false"
                                        @update:modelValue="setFontFamily($event)"
                                    />
                                </div>

                                <div v-if="previewSize === 'desktop'">
                                    <div class="flex justify-between space-x-4 mb-1.5">
                                        <input-label :label="_mft('setting:appearance.header.navigationFontSize')"></input-label>
                                        <div>
                                            <button-default
                                                @click="settings.desktop.navItemFontSize = 14"
                                                size="xs"
                                            >
                                                <i class="fa-regular fa-rotate-left"></i>
                                            </button-default>
                                        </div>
                                    </div>
                                    <div class="flex space-x-2 items-center mb-4">
                                        <div class="w-full flex space-x-2 items-center">
                                            <input-range
                                                v-model:content="settings.desktop.navItemFontSize"
                                                min="4"
                                                max="32"
                                                class="w-full mt-1"
                                            ></input-range>
                                            <input-number
                                                class="w-24 !mb-0"
                                                input-class="text-xs"
                                                v-model:content="settings.desktop.navItemFontSize"
                                            ></input-number>
                                        </div>
                                    </div>
                                </div>

                                <div v-if="previewSize === 'mobile'">
                                    <div class="flex justify-between space-x-4 mb-1.5">
                                        <input-label :label="_mft('setting:appearance.header.navigationFontSize')"></input-label>
                                        <div>
                                            <button-default
                                                @click="settings.mobile.navItemFontSize = 15"
                                                size="xs"
                                            >
                                                <i class="fa-regular fa-rotate-left"></i>
                                            </button-default>
                                        </div>
                                    </div>
                                    <div class="flex space-x-2 items-center mb-4">
                                        <div class="w-full">
                                            <input-range
                                                v-model:content="settings.mobile.navItemFontSize"
                                                min="4"
                                                max="32"
                                                class="w-full mt-1"
                                            ></input-range>
                                            <input-number
                                                class="w-24 !mb-0"
                                                input-class="text-xs"
                                                v-model:content="settings.mobile.navItemFontSize"
                                            ></input-number>
                                        </div>
                                    </div>
                                </div>

                                <hr class="-mx-4 mt-4">
                                <h5 class="mt-4 mb-2">{{ _mft('setting:appearance.theme.colors') }}</h5>
                                <div>
                                    <input-label
                                        :label="_mft('setting:appearance.header.backgroundColor')"
                                        class="mb-1"
                                    ></input-label>
                                    <div class="flex space-x-2">
                                        <div
                                            class="w-full h-8 border rounded-md cursor-pointer hover:border-gray-300"
                                            :style="{backgroundColor: settings.navBarBgColor}"
                                            @click="selectingNavBarBgColor = true"
                                        ></div>
                                        <button-default
                                            v-if="selectingNavBarBgColor === true"
                                            size="sm"
                                            @click="selectingNavBarBgColor = false"
                                        >
                                            {{ _mft('shared:done') }}
                                        </button-default>
                                    </div>
                                    <div
                                        v-if="selectingNavBarBgColor === true"
                                        class="p-2 border mt-2 -mx-2 rounded-md"
                                    >
                                        <color-picker
                                            v-if="selectingNavBarBgColor"
                                            :color="settings.navBarBgColor"
                                            :debounce="100"
                                            :show-palette="true"
                                            :disable-alpha="false"
                                            format="prgb"
                                            @colorChanged="settings.navBarBgColor = $event"
                                        ></color-picker>
                                    </div>
                                </div>

                                <div class="mt-4">
                                    <input-label
                                        :label="_mft('setting:appearance.header.textColor')"
                                        class="mb-1"
                                    ></input-label>
                                    <div class="flex space-x-2">
                                        <div
                                            class="w-full h-8 border rounded-md cursor-pointer hover:border-gray-300"
                                            :style="{backgroundColor: settings.navBarTextColor}"
                                            @click="selectingNavBarTextColor = true"
                                        ></div>
                                        <button-default
                                            v-if="selectingNavBarTextColor === true"
                                            size="sm"
                                            @click="selectingNavBarTextColor = false"
                                        >
                                            {{ _mft('shared:done') }}
                                        </button-default>
                                    </div>
                                    <div
                                        v-if="selectingNavBarTextColor === true"
                                        class="p-2 border mt-2 -mx-2 rounded-md"
                                    >
                                        <color-picker
                                            v-if="selectingNavBarTextColor"
                                            :color="settings.navBarTextColor"
                                            :debounce="100"
                                            :show-palette="true"
                                            format="hex"
                                            @colorChanged="settings.navBarTextColor = $event"
                                        ></color-picker>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="w-full bg-gray-200">
                            <div class="bg-white h-12 p-2 border-b flex justify-between items-center relative ">
                                <div class="absolute -left-px top-0 bottom-0 w-px bg-white"></div>
                                <div></div>
                                <div>
                                    <button-bar
                                        :responsive="false"
                                        size="sm"
                                        :options="[
                                            { label: _mft('setting:appearance.desktop'), name: 'desktop' },
                                            { label: _mft('setting:appearance.mobile'), name: 'mobile' },
                                        ]"
                                        :selectedOptionName="previewSize"
                                        @select="previewSize = $event.name"
                                        class="!mb-0"
                                    />
                                </div>
                                <div class="flex justify-end items-center space-x-2">
                                    <button-base
                                        size="sm"
                                        @click="cancel"
                                    >
                                        {{ _mft('shared:action.exit') }}
                                    </button-base>
                                    <button-success
                                        size="sm"
                                        @click="submit"
                                        :state="savingState"
                                    >
                                        {{ _mft('shared:action.save') }}
                                    </button-success>
                                </div>
                            </div>


                            <div class="w-full h-full bg-gray-50 relative" :class="previewSize === 'mobile' ? 'max-w-md mx-auto' : ''">
                                <component :is="selectedThemeComponent"
                                    :logo-url="logoUrl"
                                    :sidebar-navigation="navigation"
                                    navigation-style="fullwidth"
                                    :user-logged-in="props.location === 'customer_area'"
                                    :account-creation-enabled="accountCreationEnabled"
                                    :theme="currentTheme"
                                    :theme-settings="settings"
                                    :preview="true"
                                ></component>
                                <div class="bg-gradient-to-br from-gray-200 to-slate-400 w-full aspect-video max-h-[30vh]"></div>
                                <div class="px-4 max-w-4xl mx-auto">
                                    <h1 class="mt-4 mb-2">Example content</h1>
                                    <p>Laborum non labore ullamco id sint commodo ea occaecat sint qui pariatur commodo sit quis cillum. Commodo ad excepteur voluptate commodo. Anim fugiat aute duis qui magna. Non ullamco deserunt ipsum cillum nostrud. Velit minim ea ullamco id. Commodo commodo consectetur culpa velit id nulla commodo. Excepteur labore reprehenderit ullamco nulla commodo excepteur enim.</p>

                                    <p>Proident esse tempor adipisicing aute commodo labore sit. Est occaecat ea dolor fugiat deserunt aute culpa veniam velit. Laborum quis minim enim id eu sit consequat officia quis Lorem mollit magna quis et. Laboris aliquip mollit nulla deserunt cupidatat sit ex esse sunt officia magna elit. Officia et nulla adipisicing cillum eiusmod magna sint voluptate deserunt. Cupidatat ipsum ut elit occaecat veniam qui tempor eiusmod consectetur magna in in ullamco eiusmod. Consequat adipisicing cupidatat nulla aliqua excepteur. Laborum ex duis dolore aliquip non ullamco sunt.</p>

                                    <p>Esse dolore esse cupidatat ea adipisicing laborum pariatur nulla consequat nulla occaecat culpa irure non. Adipisicing enim aute cillum cillum eu cupidatat ipsum commodo magna in culpa nostrud sit sint quis. Deserunt aliqua magna deserunt ut elit dolore Lorem dolor cillum magna esse. Nisi nostrud eiusmod dolore dolor laborum occaecat consectetur et sit. Ut enim commodo consectetur consequat esse dolore dolore. Labore irure tempor pariatur ad laboris laboris officia amet commodo anim consequat veniam.</p>

                                    <p>Veniam nulla laboris nisi excepteur aute quis elit officia magna excepteur labore anim minim incididunt do. Dolor nulla eu occaecat cillum quis voluptate ut exercitation incididunt sint labore sit. Voluptate quis voluptate eu magna officia irure labore nisi voluptate nisi eu ut occaecat occaecat deserunt. Deserunt qui laboris laboris laborum ea adipisicing enim fugiat est occaecat reprehenderit culpa aliquip minim dolor. Exercitation nisi in est nulla est velit magna minim adipisicing ex fugiat mollit.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>

        <h3>{{ title }}</h3>
        <div class="flex justify-between items-center space-x-4">
            <span>{{ currentThemeName }}</span>
            <button-base @click="editingTheme = true">Edit theme</button-base>
        </div>
    </div>
</template>

<style scoped>

</style>
